import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  Home,
  ArrowLeft,
  Search,
  RefreshCw,
  Package,
  ShoppingBag,
} from "lucide-react";

const ProductNotFound = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isVisible, setIsVisible] = useState(false);
  const [currentText, setCurrentText] = useState("Product not found...");

  const errorTexts = ["Product not found...", "Product not in stock..."];

  useEffect(() => {
    // Trigger entrance animation
    setIsVisible(true);

    // Change error text periodically
    const textInterval = setInterval(() => {
      setCurrentText((prev) => {
        const currentIndex = errorTexts.indexOf(prev);
        const nextIndex = (currentIndex + 1) % errorTexts.length;
        return errorTexts[nextIndex];
      });
    }, 2000);

    return () => {
      clearInterval(textInterval);
    };
  }, []);

  const handleGoHome = () => {
    navigate("/");
  };

  const handleGoBack = () => {
    window.history.length > 1 ? navigate(-1) : navigate("/");
  };

  const handleBrowseProducts = () => {
    navigate("/products");
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-orange-50 to-orange-200 relative overflow-hidden">
      {/* Animated Background Particles */}
      <div className="absolute inset-0">
        {[...Array(25)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-gradient-to-r from-red-500 to-orange-400 rounded-full opacity-40"
            initial={{
              x:
                typeof window !== "undefined"
                  ? Math.random() * window.innerWidth
                  : Math.random() * 1200,
              y:
                typeof window !== "undefined"
                  ? Math.random() * window.innerHeight
                  : Math.random() * 800,
              scale: 0,
            }}
            animate={{
              x:
                typeof window !== "undefined"
                  ? Math.random() * window.innerWidth
                  : Math.random() * 1200,
              y:
                typeof window !== "undefined"
                  ? Math.random() * window.innerHeight
                  : Math.random() * 800,
              scale: [0, 1, 0],
            }}
            transition={{
              duration: Math.random() * 4 + 3,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Geometric Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <svg width="100%" height="100%" className="absolute inset-0">
          <defs>
            <pattern
              id="product-error-grid"
              width="80"
              height="80"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 80 0 L 0 0 0 80"
                fill="none"
                stroke="url(#product-error-gradient)"
                strokeWidth="1"
              />
            </pattern>
            <linearGradient
              id="product-error-gradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#ef4444" />
              <stop offset="50%" stopColor="#f97316" />
              <stop offset="100%" stopColor="#eab308" />
            </linearGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#product-error-grid)" />
        </svg>
      </div>

      {/* Main Content Container */}
      <div className="relative flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
        {/* Product ID Display */}
        {id && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mb-4 px-4 py-2 bg-white bg-opacity-70 rounded-full border border-gray-200 backdrop-blur-sm"
          >
            <span className="text-sm text-gray-600">
              Product ID:{" "}
              <span className="font-semibold text-gray-800">{id}</span>
            </span>
          </motion.div>
        )}

        {/* 404 Number with Product Icon */}
        <motion.div
          initial={{ scale: 0, rotate: -180, opacity: 0 }}
          animate={{ scale: isVisible ? 1 : 0, rotate: 0, opacity: 1 }}
          transition={{
            duration: 1.5,
            ease: "easeOut",
            type: "spring",
            stiffness: 80,
          }}
          className="relative mb-6 sm:mb-8 flex items-center justify-center text-center"
        >
          {/* Glitch Background */}
          <motion.div
            className="absolute inset-0 text-6xl flex items-center justify-center text-center  sm:text-7xl md:text-8xl lg:text-9xl font-black text-red-500 opacity-20 blur-sm"
            animate={{
              x: [0, -2, 2, 0],
              y: [0, 1, -1, 0],
            }}
            transition={{
              duration: 0.2,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          >
            Product Not Found
          </motion.div>

          {/* Main 404 Text */}
          <motion.h1
            className="relative z-10 flex items-center justify-center text-center  text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 bg-clip-text text-transparent"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            Product Not Found
          </motion.h1>

          {/* Floating Product Icons */}
          {[Package, ShoppingBag].map((Icon, i) => (
            <motion.div
              key={i}
              className="absolute w-8 h-8 sm:w-10 sm:h-10 text-red-500 opacity-60"
              style={{
                left: `${50 + Math.cos((i * 180 * Math.PI) / 180) * (window.innerWidth < 640 ? 100 : 140)}px`,
                top: `${50 + Math.sin((i * 180 * Math.PI) / 180) * (window.innerWidth < 640 ? 100 : 140)}px`,
              }}
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.3, 0.8, 0.3],
                rotate: 360,
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 1,
                ease: "easeInOut",
              }}
            >
              <Icon className="w-full h-full" />
            </motion.div>
          ))}
        </motion.div>

        {/* Logo Container */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="relative mb-6 sm:mb-8"
        >
          <motion.div
            className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 flex bg-black/[0.2] items-center justify-center rounded-lg"
            whileHover={{ scale: 1.1, rotate: 5 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <img
              src="https://cdn.acsdev.in/ManaPrint/manaprint_new.png"
              alt="ManaPrint Logo"
              className="w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 object-contain filter drop-shadow-xl opacity-80"
            />
          </motion.div>
        </motion.div>

        {/* Error Message */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="text-center mb-8 sm:mb-12 relative z-20"
        >
          {/* Dynamic Error Text */}
          <AnimatePresence mode="wait">
            <motion.p
              key={currentText}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
              className="text-gray-600 text-base sm:text-lg md:text-xl mb-6 max-w-md mx-auto"
            >
              {currentText}
            </motion.p>
          </AnimatePresence>

          {/* <motion.p
            className="text-gray-500 text-sm sm:text-base max-w-lg mx-auto leading-relaxed"
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            The product you're looking for seems to have vanished from our
            catalog. Don't worry, we have plenty of other amazing products
            waiting for you!
          </motion.p> */}
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 sm:gap-6 relative z-20"
        >
          {/* Browse Products Button */}
          <motion.button
            onClick={handleBrowseProducts}
            className="flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 min-w-[180px]"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <ShoppingBag className="w-5 h-5" />
            Browse Products
          </motion.button>

          {/* Go Home Button */}
          <motion.button
            onClick={handleGoHome}
            className="flex items-center justify-center gap-3 px-6 py-3 bg-white text-gray-700 font-semibold rounded-full shadow-lg hover:shadow-xl border-2 border-gray-200 hover:border-orange-300 transition-all duration-300 min-w-[160px]"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <Home className="w-5 h-5" />
            Go Home
          </motion.button>

          {/* Go Back Button */}
          <motion.button
            onClick={handleGoBack}
            className="flex items-center justify-center gap-3 px-6 py-3 bg-white text-gray-700 font-semibold rounded-full shadow-lg hover:shadow-xl border-2 border-gray-200 hover:border-orange-300 transition-all duration-300 min-w-[160px]"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft className="w-5 h-5" />
            Go Back
          </motion.button>
        </motion.div>

        {/* Animated Dots */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="flex space-x-2 mt-8 relative z-20"
        >
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="w-2 h-2 sm:w-3 sm:h-3 bg-gradient-to-r from-red-500 to-orange-400 rounded-full"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: i * 0.3,
                ease: "easeInOut",
              }}
            />
          ))}
        </motion.div>
      </div>

      {/* Corner Decorative Elements */}
      <motion.div
        className="absolute top-4 sm:top-6 lg:top-10 left-4 sm:left-6 lg:left-10 w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 border-l-2 sm:border-l-3 lg:border-l-4 border-t-2 sm:border-t-3 lg:border-t-4 border-red-500 opacity-30"
        animate={{ scale: [1, 1.1, 1], rotate: [0, 5, 0] }}
        transition={{ duration: 4, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-4 sm:bottom-6 lg:bottom-10 right-4 sm:right-6 lg:right-10 w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 border-r-2 sm:border-r-3 lg:border-r-4 border-b-2 sm:border-b-3 lg:border-b-4 border-orange-400 opacity-30"
        animate={{ scale: [1, 1.1, 1], rotate: [0, -5, 0] }}
        transition={{ duration: 4, repeat: Infinity, delay: 2 }}
      />

      {/* Floating Product Elements */}
      <motion.div
        className="absolute top-1/4 right-8 sm:right-16 lg:right-24"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.2 }}
        transition={{ delay: 1 }}
      >
        <motion.div
          className="w-8 h-8 sm:w-12 sm:h-12 border-4 border-red-400 rounded-lg"
          animate={{
            rotate: [0, 10, -10, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 3, repeat: Infinity }}
        />
        <motion.div
          className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-orange-400 rounded-lg mt-2 ml-4"
          animate={{
            rotate: [0, -10, 10, 0],
            scale: [1, 0.9, 1],
          }}
          transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
        />
      </motion.div>

      {/* Mobile-specific floating elements */}
      <div className="sm:hidden">
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={`mobile-product-error-${i}`}
            className="absolute w-1 h-1 bg-gradient-to-r from-red-500 to-orange-400 rounded-full opacity-30"
            style={{
              left: `${15 + i * 20}%`,
              top: `${15 + i * 15}%`,
            }}
            animate={{
              scale: [1, 2, 1],
              opacity: [0.3, 0.7, 0.3],
              y: [0, -10, 0],
            }}
            transition={{
              duration: 2 + i * 0.3,
              repeat: Infinity,
              delay: i * 0.4,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <style jsx>{`
        /* Glitch effect for 404 text */
        @keyframes glitch {
          0% {
            transform: translate(0);
          }
          20% {
            transform: translate(-2px, 2px);
          }
          40% {
            transform: translate(-2px, -2px);
          }
          60% {
            transform: translate(2px, 2px);
          }
          80% {
            transform: translate(2px, -2px);
          }
          100% {
            transform: translate(0);
          }
        }

        .glitch {
          animation: glitch 0.3s infinite;
        }

        /* Responsive adjustments */
        @media (max-width: 640px) {
          .tracking-widest {
            letter-spacing: 0.1em;
          }
        }

        /* Reduced motion preferences */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .bg-gradient-to-r {
            background: #000 !important;
            color: #fff !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductNotFound;
